import { Link, useLocation, useNavigate } from "react-router-dom";
import BrandLogo from "@/components/feature/BrandLogo";
import { supabase } from "@/lib/supabase";
import { ROLE_META } from "../adminConfig";
import { useAdminSession } from "./AdminSessionContext";

interface NavItem {
  label: string;
  to: string;
  icon: string;
  exact: boolean;
  adminOnly: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: "会员列表", to: "/admin", icon: "ri-contacts-book-2-line", exact: true, adminOnly: false },
  { label: "手动添加", to: "/admin/new", icon: "ri-user-add-line", exact: false, adminOnly: true },
  { label: "一键导入", to: "/admin/import", icon: "ri-upload-cloud-2-line", exact: false, adminOnly: true },
  { label: "员工管理", to: "/admin/staff", icon: "ri-team-line", exact: false, adminOnly: true },
];

interface AdminSidebarProps {
  onNavigate?: () => void;
}

export default function AdminSidebar({ onNavigate }: AdminSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, username } = useAdminSession();

  const isAdmin = role === "admin";
  const roleMeta = role ? ROLE_META[role] : null;
  const items = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="flex h-full flex-col bg-background-50">
      <div className="flex h-16 items-center border-b border-background-200 px-5">
        <Link to="/admin" className="cursor-pointer" onClick={onNavigate}>
          <BrandLogo size="sm" />
        </Link>
      </div>

      <div className="px-4 pt-5">
        <p className="px-3 pb-2 text-xs font-medium tracking-wider text-foreground-400">
          运营管理
        </p>
        <nav className="flex flex-col gap-1">
          {items.map((item) => {
            const active = item.exact
              ? location.pathname === item.to
              : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
                }`}
              >
                <span className="flex h-6 w-6 items-center justify-center">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="mt-auto border-t border-background-200 p-4">
        <div className="mb-3 rounded-md bg-background-100 px-3 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className={`${roleMeta?.icon || "ri-user-line"} text-sm`}></i>
            </span>
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-foreground-800">
                {roleMeta?.label || "未分配"}
              </p>
              <p className="truncate text-xs text-foreground-400">
                {username || "—"}
              </p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-900"
        >
          <span className="flex h-6 w-6 items-center justify-center">
            <i className="ri-logout-box-r-line text-lg"></i>
          </span>
          退出登录
        </button>
      </div>
    </div>
  );
}